/**
 * client/src/main.tsx — the entry point `client/index.html` loads.
 *
 * Deliberately four lines of work: mount, and put the session provider above everything so that no
 * screen can render before `GET /api/v1/session` has been *asked* (§6.1). The structural CSS that
 * pagination depends on — `position: fixed`, `overflow: hidden`, `-webkit-text-size-adjust: 100%` —
 * is inline in `index.html` on purpose (§5.2 pitfalls 1 and 4): it must be in effect before the
 * first paint and before `Paginator` measures anything, which is earlier than this file runs.
 */

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App.tsx";
import { AuthProvider } from "./lib/auth.tsx";
import { startOfflineRuntime } from "./lib/outbox.ts";
import "./ui/app.css";

/**
 * P6, §5.8. Registers the service worker, drains anything written while offline, and publishes the
 * one answer to "is the network there?" that the audio engine reads.
 *
 * Above `createRoot` and outside React on purpose: the outbox must drain whether or not a screen
 * that cares about it is ever mounted, and `StrictMode` double-invokes effects — a registration or a
 * flush hung off a component would run twice on every mount in development.
 */
startOfflineRuntime();

const root = document.getElementById("root");
if (!root) {
  // index.html carries <div id="root">. If it is missing, the shell was edited or a proxy served
  // something else entirely — say which, because a silent blank page here looks like a JS error.
  throw new Error("#root is missing from index.html — the app shell was not served.");
}

createRoot(root).render(
  <StrictMode>
    <AuthProvider>
      <App />
    </AuthProvider>
  </StrictMode>,
);
